// src/pages/Home.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PiShoppingCartSimpleLight } from "react-icons/pi";
import { IoIosHeartEmpty } from "react-icons/io";
import { TbHandClick } from "react-icons/tb";
import OfflineOverlay from "../components/OfflineOverlay";
import { readSearchHistory } from "../utils/searchHistory";
import { fetchCategories, selectCategories } from "../app/features/category/categorySlice";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

import {
  getAllProducts,
  addToWishlists,
  filterProducts,
  setSearchTerm,
} from "../app/features/products/productSlice";
import { getAllPosts } from "../app/features/Posts/postSlice";
import { getAllPosters } from "../app/features/posters/posterSlice";

// ✅ ADD TO CART (DB for logged-in, localStorage for guests)
import { addProdToCart, getUserCart as fetchUserCart } from "../app/features/user/userSlice";

import heroMain1 from "../images/banner/home-1.jpeg";
import heroMain2 from "../images/banner/home-6.jpg";
import heroMain3 from "../images/banner/home-7.jpg";

import appheroMain1 from "../images/banner/appimage2.jpg";
import appheroMain2 from "../images/banner/appimage1.jpg";
import appheroMain3 from "../images/banner/appimage.jpg";

import sideTop1 from "../images/banner/home-5.jpeg";
import sideTop2 from "../images/banner/home-2.jpeg";

import sideBottom1 from "../images/banner/home-33.jpeg";
import sideBottom2 from "../images/banner/home-44.jpeg";

import product1 from "../images/products/stamp.jpg";

// promo banners
import promoTopLeft from "../images/banner/catBanner-4.jpg";
import promoTopMiddle from "../images/banner/catBanner-5.jpg";
import promoBottomLeft from "../images/banner/catBanner-3.jpg";
import promoBottomMiddle from "../images/banner/catBanner-2.jpg";
import promoTallRight from "../images/banner/catBanner-1.jpg";

/* ========================= HELPERS ========================= */
const stripHtml = (html) => (html ? html.replace(/<\/?[^>]+(>|$)/g, "") : "");
const normalize = (v) => String(v ?? "").toLowerCase().trim();

const hasTag = (p, tagText) => {
  const tags = Array.isArray(p?.tags) ? p.tags : [];
  const wanted = normalize(tagText);
  return tags.some((t) => normalize(t) === wanted);
};

const productHasCategoryId = (p, wantedId) => {
  if (!wantedId) return false;
  const cats = Array.isArray(p?.category) ? p.category : [];
  return cats.some((c) => {
    if (typeof c === "object" && c) return String(c?._id) === String(wantedId);
    return String(c) === String(wantedId);
  });
};

const getFirstImageUrl = (images) => {
  if (!images) return "";
  if (Array.isArray(images)) {
    const first = images[0];
    if (!first) return "";
    if (typeof first === "string") return first;
    return first?.url || "";
  }
  return "";
};

// ✅ Price helpers
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatUGX = (n) => `UGX ${toNum(n).toLocaleString()}`;

const resolvePriceUI = (p = {}, fallbackPrice = 0) => {
  const price = toNum(p?.price ?? fallbackPrice);
  const disc = toNum(p?.discountedPrice);

  const hasDiscount = disc > 0 && price > 0 && disc < price;

  if (hasDiscount) {
    return {
      now: disc,
      old: price,
      priceText: `UGX (${disc}-${price})`,
      oldText: "",
      isDiscount: true,
    };
  }

  return {
    now: price,
    old: 0,
    priceText: formatUGX(price),
    oldText: "",
    isDiscount: false,
  };
};

const singularizeLoose = (word) => {
  const w = String(word || "").trim();
  if (!w) return "";
  if (w.length <= 3) return w;
  if (w.endsWith("ies") && w.length > 4) return w.slice(0, -3) + "y";
  if (w.endsWith("es") && w.length > 4) return w.slice(0, -2);
  if (w.endsWith("s") && w.length > 3) return w.slice(0, -1);
  return w;
};

const buildLooseSearch = (title) => {
  const t = String(title || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const tokens = t.split(" ").map(singularizeLoose).filter(Boolean);
  return tokens.join(" ").trim();
};

const getPostImageUrl = (img) => img?.imageUrl || img?.url || img?.secure_url || img || "";

const flattenPostImages = (docs = []) => {
  const arr = Array.isArray(docs) ? docs : [];
  return arr.flatMap((doc) => {
    const imgs = Array.isArray(doc?.images) ? doc.images : [];
    return imgs
      .slice()
      .sort((a, b) => Number(a?.order || 0) - Number(b?.order || 0))
      .map((img, idx) => ({
        _key: `${doc?._id || "doc"}-${img?._id || idx}`,
        parentId: doc?._id,
        title: img?.caption || doc?.title || "Post",
        link: img?.link || "",
        imageUrl: getPostImageUrl(img),
      }));
  });
};

/* ========================= CROSSFADE IMAGE ========================= */
const FadeImage = ({
  src,
  alt = "",
  className = "",
  duration = 520,
  background = "#0b0b0b",
  objectFit = "cover",
  fallbackSrc = "",
  loaderSize = 22,
  loaderClassName = "",
}) => {
  const [curr, setCurr] = useState(src);
  const [prev, setPrev] = useState(null);
  const [isFading, setIsFading] = useState(false);
  const [loaded, setLoaded] = useState(true);
  const tRef = useRef(null);

  useEffect(() => {
    if (!src || src === curr) return;

    let cancelled = false;
    setLoaded(false);

    const img = new Image();
    img.src = src;

    img.onload = () => {
      if (cancelled) return;

      setPrev(curr);
      setCurr(src);
      setIsFading(true);
      setLoaded(true);

      if (tRef.current) clearTimeout(tRef.current);
      tRef.current = setTimeout(() => {
        setPrev(null);
        setIsFading(false);
        tRef.current = null;
      }, duration + 60);
    };

    img.onerror = () => {
      if (cancelled) return;

      if (fallbackSrc) {
        setPrev(curr);
        setCurr(fallbackSrc);
        setIsFading(true);
        setLoaded(true);

        if (tRef.current) clearTimeout(tRef.current);
        tRef.current = setTimeout(() => {
          setPrev(null);
          setIsFading(false);
          tRef.current = null;
        }, duration + 60);
      } else {
        setLoaded(true);
      }
    };

    return () => {
      cancelled = true;
      if (tRef.current) clearTimeout(tRef.current);
    };
  }, [src, curr, duration, fallbackSrc]);

  useEffect(() => () => tRef.current && clearTimeout(tRef.current), []);

  useEffect(() => {
    setLoaded(Boolean(curr));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onImgError = (e) => {
    if (fallbackSrc && e?.currentTarget?.src !== fallbackSrc) {
      e.currentTarget.src = fallbackSrc;
    }
  };

  return (
    <div
      className={`fadeimg ${className}`}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        background,
      }}
    >
      {!loaded && (
        <div
          className={`fadeimg-spinner ${loaderClassName}`}
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            zIndex: 3,
            pointerEvents: "none",
          }}
          aria-hidden="true"
        >
          <AiOutlineLoading3Quarters size={loaderSize} className="imgload-spin" />
        </div>
      )}

      {prev && (
        <img
          src={prev}
          alt=""
          aria-hidden="true"
          onError={onImgError}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit,
            opacity: 1,
          }}
        />
      )}

      <img
        src={curr}
        alt={alt}
        onError={onImgError}
        onLoad={() => {
          setLoaded(true);
          if (prev) requestAnimationFrame(() => setIsFading(false));
        }}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit,
          opacity: prev ? (isFading ? 0 : 1) : 1,
          transition: prev ? `opacity ${duration}ms ease` : undefined,
        }}
      />
    </div>
  );
};

const ImgWithLoader = ({
  src,
  alt = "",
  className = "",
  wrapClassName = "",
  fallbackSrc = "/images/placeholder.png",
  style,
  imgStyle,
  loaderSize = 20,
  loaderClassName = "",
  onClick,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const imgRef = useRef(null);

  const hasSrc = Boolean(String(src || "").trim());
  const finalSrc = !hasSrc || failed ? fallbackSrc : src;

  useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [finalSrc]);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    if (img.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [finalSrc]);

  return (
    <div
      className={`imgload-wrap ${wrapClassName}`}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "block",
        ...style,
      }}
      onClick={onClick}
    >
      {!loaded && (
        <div
          className={`imgload-spinner ${loaderClassName}`}
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            zIndex: 2,
            pointerEvents: "none",
          }}
          aria-hidden="true"
        >
          <AiOutlineLoading3Quarters size={loaderSize} className="imgload-spin" />
        </div>
      )}

      <img
        key={finalSrc}
        ref={imgRef}
        src={finalSrc}
        alt={alt}
        className={className}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          opacity: loaded ? 1 : 0,
          transition: "opacity 180ms ease",
          ...imgStyle,
        }}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => {
          if (hasSrc && !failed) setFailed(true);
          setLoaded(true);
        }}
      />
    </div>
  );
};

/* ========================= APP CATEGORY PILLS ========================= */
const AppCategorySection = ({ items = [], defaultActive = "", onSelect }) => {
  const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const firstKey = list?.[0]?.key || "";
  const [active, setActive] = useState(defaultActive || firstKey);

  useEffect(() => {
    if (!list.length) return;
    const exists = list.some((x) => String(x.key) === String(active));
    if (!exists) setActive(defaultActive || list[0]?.key);
  }, [list, active, defaultActive]);

  const pick = (it) => {
    setActive(it.key);
    onSelect?.(it);
  };

  return (
    <section className="app-cats">
      <div className="app-cats-row" role="tablist" aria-label="App categories">
        {list.map((it) => {
          const isActive = String(it.key) === String(active);

          return (
            <button
              key={it.key}
              type="button"
              className={`app-cat-pill ${isActive ? "is-active" : ""}`}
              onClick={() => pick(it)}
              role="tab"
              aria-selected={isActive ? "true" : "false"}
            >
              <span className="app-cat-txt">{it.label}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

const AppCategorySkeleton = ({ count = 7 }) => (
  <section className="app-cats" aria-label="Loading categories">
    <div className="app-cats-row">
      {Array.from({ length: count }).map((_, i) => (
        <div key={`app-cat-skel-${i}`} className="app-cat-pill app-cat-pill--skel" aria-hidden="true">
          <div className="skel skel-pill" />
        </div>
      ))}
    </div>
  </section>
);

// ✅ Desktop hero slides
const MAIN_SLIDES = [{ src: heroMain1 }, { src: heroMain2 }, { src: heroMain3 }];
// ✅ App/mobile hero slides
const APP_MAIN_SLIDES = [{ src: appheroMain1 }, { src: appheroMain2 }, { src: appheroMain3 }];

const SIDE_TOP_IMAGES = [sideTop1, sideTop2];
const SIDE_BOTTOM_IMAGES = [sideBottom1, sideBottom2];

const HERO_OVERLAY = {
  title: "Printing And Stationery",
  subtitle: "on your smartphone with just one click",
  align: "left",
};

const SERVICE_ITEMS = [
  { icon: "🚚", title: "Fast Shipping", subtitle: "All orders over $500" },
  { icon: "💬", title: "Support 24/7", subtitle: "Shop with an expert" },
  { icon: "🎁", title: "Gift Voucher", subtitle: "Save up to 25% off" },
  { icon: "🏷️", title: "Affordable Prices", subtitle: "Get factory price" },
  { icon: "🔒", title: "Secure Payments", subtitle: "100% protected" },
];

const TAB_LABELS = [
  { key: "featured", label: "FEATURED" },
  { key: "bestseller", label: "DIGITAL PRINT" },
  { key: "latest", label: "STATIONERY" },
];

/* ✅ fallback now has 6 items */
const FEATURED_DEALS_FALLBACK = [
  {
    id: 1,
    slug: "",
    main: true,
    name: "Large Format Print Deal",
    img: product1,
    price: "UGX 129,000",
    oldPrice: "UGX 199,000",
    sold: "920",
    bullets: ["High quality prints", "Fast turnaround", "Sharp colors", "Professional finishing"],
    smallImages: [product1, product1, product1, product1],
  },
  { id: 2, slug: "", name: "Promo Deal", img: product1, price: "UGX 78,000", oldPrice: "UGX 89,000", sold: "529" },
  { id: 3, slug: "", name: "Banner Deal", img: product1, price: "UGX 189,000", oldPrice: "UGX 249,000", sold: "420" },
  { id: 4, slug: "", name: "Poster Deal", img: product1, price: "UGX 69,000", oldPrice: "UGX 79,000", sold: "387" },
  { id: 5, slug: "", name: "Sticker Deal", img: product1, price: "UGX 55,000", oldPrice: "UGX 64,000", sold: "312" },
  { id: 6, slug: "", name: "Roll Up Banner", img: product1, price: "UGX 95,000", oldPrice: "UGX 120,000", sold: "280" },
];

const Skeleton = ({ className = "" }) => <div className={`skel ${className}`} />;

const PopularSkeletonGrid = ({ count = 10 }) => (
  <div className="hp-popGrid">
    {Array.from({ length: count }).map((_, i) => (
      <div className="hp-popCard" key={`pop-skel-${i}`}>
        <div className="hp-popTop">
          <Skeleton className="skel-square skel-img" />
        </div>
        <div className="hp-popBody">
          <Skeleton className="skel-line skel-line--lg" />
          <Skeleton className="skel-line skel-line--sm" />
        </div>
      </div>
    ))}
  </div>
);

const CategorySkeletonGrid = ({ count = 10 }) => (
  <div className="pop-grid kcats-grid">
    {Array.from({ length: count }).map((_, i) => (
      <article className="kcats-card" key={`cat-skel-${i}`}>
        <div className="kcats-text" style={{ width: "70%" }}>
          <Skeleton className="skel-line skel-line--md" />
          <Skeleton className="skel-line skel-line--sm" />
        </div>
        <div className="kcats-img-wrap">
          <Skeleton className="skel-square skel-icon" />
        </div>
      </article>
    ))}
  </div>
);

const TrendSkeletonRow = ({ count = 10 }) => (
  <div className="trend-track">
    {Array.from({ length: count }).map((_, i) => (
      <article className="trend-card" key={`trend-skel-${i}`}>
        <div className="trend-img-wrap">
          <Skeleton className="skel-square skel-img" />
        </div>
        <div className="hp-popBody1">
          <Skeleton className="skel-line skel-line--md" />
          <Skeleton className="skel-line skel-line--sm" />
        </div>
      </article>
    ))}
  </div>
);

const LatestSkeletonRow = ({ count = 4 }) => (
  <div className="latest-track">
    {Array.from({ length: count }).map((_, i) => (
      <article className="latest-card" key={`post-skel-${i}`}>
        <div className="latest-image-wrap">
          <Skeleton className="skel-fill" />
        </div>
        <div className="latest-meta">
          <Skeleton className="skel-line skel-line--sm" />
          <Skeleton className="skel-line skel-line--md" />
          <Skeleton className="skel-line skel-line--md" />
          <Skeleton className="skel-btn" />
        </div>
      </article>
    ))}
  </div>
);

const Home = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isOnline, setIsOnline] = useState(() => {
    if (typeof window === "undefined") return true;
    return navigator.onLine;
  });

  const [homeFetchFailed, setHomeFetchFailed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    setIsOnline(navigator.onLine);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

 

  const authState = useSelector((s) => s?.auth || {});

  const isLoggedIn = Boolean(
    authState?.user?.token ||
      (typeof window !== "undefined" && localStorage.getItem("token"))
  );

  const GUEST_CART_KEY = "kupto_guest_cart_v1";

  const readGuestCart = useCallback(() => {
    try {
      const raw = localStorage.getItem(GUEST_CART_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, []);

  const writeGuestCart = useCallback((items) => {
    try {
      localStorage.setItem(
        GUEST_CART_KEY,
        JSON.stringify(Array.isArray(items) ? items : [])
      );
    } catch {
      // ignore
    }
  }, []);

  const addToGuestCart = useCallback(
    (payload) => {
      const list = readGuestCart();
      const pid = String(payload?.productId || "");

      const idx = list.findIndex((it) => String(it?.productId || "") === pid);

      if (idx >= 0) {
        const prevQty = Number(list[idx]?.quantity || 0);
        const addQty = Number(payload?.quantity || 0);
        const nextQty = Math.max(1, prevQty + Math.max(1, addQty));

        list[idx] = {
          ...list[idx],
          ...payload,
          quantity: nextQty,
        };
      } else {
        list.push({
          ...payload,
          addedAt: new Date().toISOString(),
        });
      }

      writeGuestCart(list);
      window.dispatchEvent(new Event("guestCartUpdated"));
    },
    [readGuestCart, writeGuestCart]
  );

  useEffect(() => {
    if (!isOnline) return;
    if (isLoggedIn) dispatch(fetchUserCart());
  }, [dispatch, isLoggedIn, isOnline]);

  const [isApp, setIsApp] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 768px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = (e) => setIsApp(Boolean(e.matches));
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    setIsApp(mq.matches);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const showStars = !isApp;

  const MAIN_LEN = useMemo(
    () => Math.max(MAIN_SLIDES.length, APP_MAIN_SLIDES.length),
    []
  );

  const [mainIndex, setMainIndex] = useState(0);
  const [topIndex, setTopIndex] = useState(0);
  const [bottomIndex, setBottomIndex] = useState(0);

  const [activeTab, setActiveTab] = useState("featured");

  const getPopularPageSize = () => {
    if (typeof window === "undefined") return 10;
    const w = window.innerWidth;
    if (w >= 2000 && w <= 4000) return 12;
    if (w >= 1600) return 10;
    if (w >= 1200) return 10;
    if (w >= 768) return 14;
    return 14;
  };

  const [POPULAR_PAGE_SIZE, setPopularPageSize] = useState(getPopularPageSize);
  const [popularPage, setPopularPage] = useState(0);

  const stationeryTrackRef = useRef(null);
  const digitalTrackRef = useRef(null);
  const recentTrackRef = useRef(null);
  const lastSearchedTrackRef = useRef(null);
  const latestTrackRef = useRef(null);

  const [openMenuKey, setOpenMenuKey] = useState(null);
  const menuWrapRef = useRef(null);
  const didInitFetchRef = useRef(false);

  const categoriesRaw = useSelector(selectCategories);
  const categories = useMemo(() => categoriesRaw ?? [], [categoriesRaw]);

  const catState = useSelector((s) => s?.category || {});
  const categoriesLoading = Boolean(catState?.isLoading);

  const productState = useSelector((s) => s?.product || {});
  const allProducts = useMemo(
    () => productState?.products ?? [],
    [productState?.products]
  );
  const productsLoading = Boolean(productState?.isLoading);

  const posterState = useSelector((s) => s?.poster || {});
  const posters = useMemo(
    () => posterState?.poster?.posters ?? [],
    [posterState?.poster?.posters]
  );
  const postsLoading = Boolean(posterState?.isLoading);

  const postState = useSelector((s) => s?.post || {});
  const posts = useMemo(() => postState?.posts ?? [], [postState?.posts]);
  const postsLoading2 = Boolean(postState?.isLoading);

  const postTiles = useMemo(() => flattenPostImages(posts), [posts]);

  const [searchHistory, setSearchHistory] = useState(() => readSearchHistory());

  useEffect(() => {
    const update = () => setSearchHistory(readSearchHistory());
    window.addEventListener("searchHistoryUpdated", update);
    return () => window.removeEventListener("searchHistoryUpdated", update);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setPopularPageSize(getPopularPageSize());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const urls = [
      ...MAIN_SLIDES.map((s) => s.src),
      ...APP_MAIN_SLIDES.map((s) => s.src),
      ...SIDE_TOP_IMAGES,
      ...SIDE_BOTTOM_IMAGES,
    ].filter(Boolean);

    const imgs = urls.map((u) => {
      const im = new Image();
      im.src = u;
      return im;
    });

    return () => void imgs;
  }, []);

  useEffect(() => {
  if (!isOnline) return;

  const fetchHomeData = async () => {
    try {
      setHomeFetchFailed(false);

      if (!didInitFetchRef.current) {
        didInitFetchRef.current = true;

        await Promise.all([
          dispatch(getAllPosters()).unwrap(),
          dispatch(getAllProducts({ page: 1, limit: 5000 })).unwrap(),
          dispatch(getAllPosts()).unwrap(),
          dispatch(fetchCategories()).unwrap(),
        ]);
      } else {
        const tasks = [];

        if (!allProducts?.length) {
          tasks.push(dispatch(getAllProducts({ page: 1, limit: 5000 })).unwrap());
        }

        if (!posters?.length) {
          tasks.push(dispatch(getAllPosters()).unwrap());
        }

        if (!posts?.length) {
          tasks.push(dispatch(getAllPosts()).unwrap());
        }

        if (!categories?.length) {
          tasks.push(dispatch(fetchCategories()).unwrap());
        }

        if (tasks.length) await Promise.all(tasks);
      }
    } catch (err) {
      console.error("Home fetch failed:", err);
      setHomeFetchFailed(true);
    }
  };

  fetchHomeData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isOnline]);

  useEffect(() => {
    const interval = setInterval(() => {
      setMainIndex((p) => (p + 1) % MAIN_LEN);
      setTopIndex((p) => (p + 1) % SIDE_TOP_IMAGES.length);
      setBottomIndex((p) => (p + 1) % SIDE_BOTTOM_IMAGES.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [MAIN_LEN]);

  useEffect(() => setPopularPage(0), [activeTab]);

  const wishlistRaw = useSelector((state) => {
    return (
      state?.auth?.wishlist ||
      state?.auth?.user?.wishlist ||
      state?.user?.wishlist ||
      state?.user?.user?.wishlist ||
      []
    );
  });

  const wishlistIdSet = useMemo(() => {
    const arr = Array.isArray(wishlistRaw) ? wishlistRaw : [];
    return new Set(
      arr
        .map((x) => (typeof x === "object" && x ? x?._id || x?.id : x))
        .filter(Boolean)
        .map((x) => String(x))
    );
  }, [wishlistRaw]);

  const getProductId = (p) => p?.raw?._id || p?._id || p?.id;
  const getProductSlug = (p) => p?.raw?.slug || p?.slug || "";

  const isInWishlist = (p) => {
    const id = getProductId(p);
    return id ? wishlistIdSet.has(String(id)) : false;
  };

  const [wishToast, setWishToast] = useState({ show: false, type: "success", text: "" });
  const toastTimerRef = useRef(null);

  const showWishToast = (text, type = "success") => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setWishToast({ show: true, text: text || "Added to wishlist", type });
    toastTimerRef.current = setTimeout(() => setWishToast({ show: false, text: "", type }), 1800);
  };

  useEffect(() => () => toastTimerRef.current && clearTimeout(toastTimerRef.current), []);

  const onWishlist = async (p) => {
    const id = getProductId(p);
    if (!id) return;

    if (!isOnline) {
      showWishToast("You are offline ❌", "error");
      return;
    }

    if (wishlistIdSet.has(String(id))) {
      showWishToast("Already in wishlist ✅", "success");
      return;
    }

    try {
      await dispatch(addToWishlists(id)).unwrap();
      showWishToast("Added to wishlist ✅", "success");
    } catch (e) {
      console.log("wishlist error:", e);
      showWishToast("Failed to add to wishlist ❌", "error");
    }
  };

  const productNeedsSelections = useCallback((prod) => {
    const p = prod?.raw || prod || {};
    const sizes = p?.size ?? p?.sizes ?? [];
    const colorsArr = p?.color ?? p?.colors ?? [];

    const hasSizes = Array.isArray(sizes) ? sizes.length > 0 : Boolean(sizes);
    const hasColors = Array.isArray(colorsArr) ? colorsArr.length > 0 : Boolean(colorsArr);

    const tags = Array.isArray(p?.tags) ? p.tags : [];
    const tagSet = new Set(tags.map((t) => String(t).toLowerCase().trim()));
    const hasFileUploadTag = tagSet.has("file upload") || tagSet.has("featured file upload");

    const isPrintable = Boolean(p?.isPrintable) || hasFileUploadTag;

    const hasPrintPricing =
      Boolean(p?.printingPrice) ||
      Boolean(p?.printPrice) ||
      Boolean(p?.printPricing) ||
      Boolean(p?.printingType) ||
      Boolean(p?.printType);

    return Boolean(hasSizes || hasColors || isPrintable || hasPrintPricing);
  }, []);

  const onAddToCart = useCallback(
    async (p) => {
      const prod = p?.raw || p;
      const id = prod?._id || prod?.id;
      const slug = prod?.slug || p?.slug || "";
      if (!id) return;

      if (productNeedsSelections(prod)) {
        if (slug) navigate(`/product/${slug}`);
        return;
      }

      if (!isOnline && isLoggedIn) {
        showWishToast("You are offline. Can't add to cart now ❌", "error");
        return;
      }

      const img = getFirstImageUrl(prod?.images) || p?.img || product1;
      const { now, old } = resolvePriceUI(prod);

      const payload = {
        productId: id,
        quantity: 1,
        price: now,
        variantImage: img,
      };

      const guestDisplay = {
        name: prod?.title || p?.name || "Product",
        slug: slug || "",
        img,
        colorLabel: "",
        sizeLabel: "",
        oldPrice: old ? old : null,
      };

      try {
        if (isLoggedIn) {
          await dispatch(addProdToCart(payload)).unwrap();
          dispatch(fetchUserCart());
        } else {
          addToGuestCart({ ...payload, ...guestDisplay });
        }
      } catch (e) {
        console.log("add to cart error:", e);
      }
    },
    [dispatch, navigate, isLoggedIn, addToGuestCart, productNeedsSelections, isOnline]
  );

  useEffect(() => {
    const onClick = (e) => {
      if (!openMenuKey) return;
      if (menuWrapRef.current && !menuWrapRef.current.contains(e.target)) {
        setOpenMenuKey(null);
      }
    };
    const onEsc = (e) => e.key === "Escape" && setOpenMenuKey(null);

    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [openMenuKey]);

  const handleCategoryClick = (cat) => {
    const id = cat?._id;
    if (!id) return;
    dispatch(setSearchTerm(""));
    dispatch(filterProducts({ category: id }));
    navigate(`/product?category=${encodeURIComponent(id)}`);
  };

  const toCard = useCallback((p) => {
    const pr = resolvePriceUI(p);

    return {
      id: p?._id,
      _id: p?._id,
      slug: p?.slug || "",
      name: p?.title || "Product",
      img: getFirstImageUrl(p?.images) || product1,
      price: pr.priceText,
      oldPrice: pr.oldText,
      rating: Math.round(Number(p?.totalrating || 0)),
      raw: p,
    };
  }, []);

  const categoryIdByTitle = useMemo(() => {
    const map = {};
    (categories || []).forEach((c) => {
      if (!c?._id || !c?.title) return;
      map[normalize(c.title)] = c._id;
    });
    return map;
  }, [categories]);

  const popularAll = useMemo(() => {
    const list = Array.isArray(allProducts) ? allProducts : [];
    const digitalId = categoryIdByTitle[normalize("Digital Printing")];
    const stationeryId = categoryIdByTitle[normalize("Stationery")];

    if (activeTab === "featured") {
      return list
        .filter((p) => hasTag(p, "Featured") || hasTag(p, "Featured File Upload"))
        .map(toCard);
    }

    if (activeTab === "bestseller") {
      return list
        .filter((p) => productHasCategoryId(p, digitalId))
        .map(toCard);
    }

    return list
      .filter((p) => productHasCategoryId(p, stationeryId))
      .map(toCard);
  }, [allProducts, activeTab, categoryIdByTitle, toCard]);

  const popularTotalPages = useMemo(
    () => Math.max(1, Math.ceil(popularAll.length / POPULAR_PAGE_SIZE)),
    [popularAll.length, POPULAR_PAGE_SIZE]
  );

  const productList = useMemo(() => {
    if (!popularAll.length) return [];
    const page = ((popularPage % popularTotalPages) + popularTotalPages) % popularTotalPages;
    const start = page * POPULAR_PAGE_SIZE;
    return popularAll.slice(start, start + POPULAR_PAGE_SIZE);
  }, [popularAll, popularPage, popularTotalPages, POPULAR_PAGE_SIZE]);

  const goPopularPrev = () => setPopularPage((p) => (p - 1 + popularTotalPages) % popularTotalPages);
  const goPopularNext = () => setPopularPage((p) => (p + 1) % popularTotalPages);

  const STATIONERY_PRODUCTS = useMemo(() => {
    const list = Array.isArray(allProducts) ? allProducts : [];
    const stationeryId = categoryIdByTitle[normalize("Stationery")];
    return list
      .filter((p) => productHasCategoryId(p, stationeryId))
      .slice(0, 12)
      .map(toCard);
  }, [allProducts, categoryIdByTitle, toCard]);

  const DIGITAL_PRINTING_PRODUCTS = useMemo(() => {
    const list = Array.isArray(allProducts) ? allProducts : [];
    const digitalId = categoryIdByTitle[normalize("Digital Printing")];
    return list
      .filter((p) => productHasCategoryId(p, digitalId))
      .map(toCard);
  }, [allProducts, categoryIdByTitle, toCard]);

  const LARGE_FORMAT_ID = useMemo(() => {
    return (
      categoryIdByTitle[normalize("Large Format Print")] ||
      categoryIdByTitle[normalize("Large Format Printing")] ||
      categoryIdByTitle[normalize("Large Format")] ||
      ""
    );
  }, [categoryIdByTitle]);

  const FEATURED_DEALS = useMemo(() => {
    const list = Array.isArray(allProducts) ? allProducts : [];
    const lf = LARGE_FORMAT_ID;
    const lfProducts = lf ? list.filter((p) => productHasCategoryId(p, lf)) : [];

    const picks = lfProducts
      .slice()
      .sort((a, b) => Number(b?.totalrating || 0) - Number(a?.totalrating || 0))
      .slice(0, 6);

    if (!picks.length) return FEATURED_DEALS_FALLBACK;

    const toDeal = (p, idx) => {
      const pr = resolvePriceUI(p);
      const images = Array.isArray(p?.images) ? p.images : [];
      const thumbs = images
        .map((im) => (typeof im === "string" ? im : im?.url))
        .filter(Boolean)
        .slice(0, 4);

      const hero = getFirstImageUrl(p?.images) || product1;

      return {
        id: p?._id,
        _id: p?._id,
        slug: p?.slug || "",
        main: idx === 0,
        name: p?.title || "Large Format Print",
        img: hero,
        price: pr.priceText,
        oldPrice: pr.oldText,
        sold: String(p?.sold ?? ""),
        bullets: ["Large format quality", "Sharp, vibrant prints", "Fast turnaround", "Professional finishing"],
        smallImages: thumbs.length ? thumbs : [hero, hero, hero, hero].slice(0, 4),
        raw: p,
      };
    };

    return picks.map(toDeal);
  }, [allProducts, LARGE_FORMAT_ID]);

  const showDealsSkeleton = productsLoading && (!FEATURED_DEALS || FEATURED_DEALS.length === 0);

  const handleViewAllLargeFormat = useCallback(() => {
    if (LARGE_FORMAT_ID) {
      dispatch(setSearchTerm(""));
      dispatch(filterProducts({ category: LARGE_FORMAT_ID }));
      navigate(`/product?category=${encodeURIComponent(LARGE_FORMAT_ID)}`);
      return;
    }
    navigate("/product");
  }, [LARGE_FORMAT_ID, dispatch, navigate]);

  const useAutoTrack = ({ trackRef, itemsLength, intervalMs = 4000 }) => {
    const [paused, setPaused] = useState(false);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);
    const userHoldRef = useRef(false);
    const holdTimerRef = useRef(null);

    const clearHold = useCallback(() => {
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
      userHoldRef.current = false;
    }, []);

    useEffect(() => () => clearHold(), [clearHold]);

    const holdAutoFor = useCallback((ms = 1400) => {
      userHoldRef.current = true;
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
      holdTimerRef.current = setTimeout(() => {
        userHoldRef.current = false;
        holdTimerRef.current = null;
      }, ms);
    }, []);

    const updateCanScroll = useCallback(() => {
      const el = trackRef.current;
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      const left = el.scrollLeft;
      setCanLeft(left > 2);
      setCanRight(max > 2 && left < max - 2);
    }, [trackRef]);

    const getStep = useCallback(() => {
      const el = trackRef.current;
      if (!el) return 320;
      const card = el.querySelector(".trend-card");
      const cardW = card ? card.getBoundingClientRect().width : 260;
      const cs = window.getComputedStyle(el);
      const gapStr = String(cs.gap || cs.columnGap || "16px");
      const gap = Number.isFinite(parseFloat(gapStr)) ? parseFloat(gapStr) : 16;
      const stepCards = window.innerWidth < 640 ? 1 : 2;
      return (cardW + gap) * stepCards;
    }, [trackRef]);

    const scrollByDir = useCallback(
      (dir = 1) => {
        const el = trackRef.current;
        if (!el) return;
        holdAutoFor(1400);
        const by = getStep() * dir;
        el.scrollBy({ left: by, behavior: "smooth" });
        window.setTimeout(updateCanScroll, 250);
        window.setTimeout(updateCanScroll, 900);
      },
      [trackRef, getStep, holdAutoFor, updateCanScroll]
    );

    useEffect(() => {
      const el = trackRef.current;
      if (!el) return;

      const onScroll = () => updateCanScroll();
      el.addEventListener("scroll", onScroll, { passive: true });

      const onResize = () => updateCanScroll();
      window.addEventListener("resize", onResize);

      updateCanScroll();

      const ro = new ResizeObserver(() => updateCanScroll());
      ro.observe(el);

      const t1 = setTimeout(updateCanScroll, 0);
      const t2 = setTimeout(updateCanScroll, 250);
      const t3 = setTimeout(updateCanScroll, 800);

      return () => {
        el.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onResize);
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
        ro.disconnect();
      };
    }, [itemsLength, trackRef, updateCanScroll]);

    useEffect(() => {
      const el = trackRef.current;
      if (!el || !itemsLength) return;

      const id = setInterval(() => {
        if (paused) return;
        if (userHoldRef.current) return;

        const max = el.scrollWidth - el.clientWidth;
        if (max <= 2) return;

        const step = getStep();
        const next = el.scrollLeft + step;

        if (next >= max - 4) el.scrollTo({ left: 0, behavior: "smooth" });
        else el.scrollTo({ left: next, behavior: "smooth" });

        window.setTimeout(updateCanScroll, 350);
      }, intervalMs);

      return () => clearInterval(id);
    }, [paused, itemsLength, intervalMs, getStep, updateCanScroll, trackRef]);

    return {
      paused,
      setPaused,
      canLeft,
      canRight,
      scrollPrev: () => scrollByDir(-1),
      scrollNext: () => scrollByDir(1),
    };
  };

  const stationerySlider = useAutoTrack({
    trackRef: stationeryTrackRef,
    itemsLength: STATIONERY_PRODUCTS.length,
    intervalMs: 4500,
  });

  const digitalSlider = useAutoTrack({
    trackRef: digitalTrackRef,
    itemsLength: DIGITAL_PRINTING_PRODUCTS.length,
    intervalMs: 4500,
  });

  const scrollTrack = (trackRef, direction) => {
    const container = trackRef.current;
    if (!container) return;
    const firstCard = container.querySelector(".trend-card");
    const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : 260;
    const gap = 16;
    const delta = (cardWidth + gap) * (direction === "next" ? 1 : -1);
    container.scrollBy({ left: delta, behavior: "smooth" });
  };

  const latestPosts = useMemo(() => {
    const arr = Array.isArray(posters) ? posters : [];
    return [...arr].sort((a, b) => {
      const da = a?.date ? new Date(a.date).getTime() : 0;
      const db = b?.date ? new Date(b.date).getTime() : 0;
      return db - da;
    });
  }, [posters]);

  const latestLoopPosts = useMemo(() => {
    if (!latestPosts.length) return [];
    return [...latestPosts, ...latestPosts];
  }, [latestPosts]);

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const goToProductsByLatestTitle = useCallback(
    (title) => {
      const q = buildLooseSearch(title);
      if (!q) {
        navigate("/product");
        return;
      }

      dispatch(setSearchTerm(q));
      dispatch(filterProducts({ search: q, q }));
      navigate(`/product?search=${encodeURIComponent(q)}`);
    },
    [dispatch, navigate]
  );

  const goToProductsByQuery = useCallback(
    (rawQuery) => {
      const q = String(rawQuery || "").trim();
      if (!q) {
        navigate("/product");
        return;
      }
      dispatch(setSearchTerm(q));
      dispatch(filterProducts({ search: q, q }));
      navigate(`/product?search=${encodeURIComponent(q)}`);
    },
    [dispatch, navigate]
  );

  const [latestPaused, setLatestPaused] = useState(false);

  useEffect(() => {
    const track = latestTrackRef.current;
    if (!track || postsLoading || latestLoopPosts.length === 0) return;

    const init = () => {
      const half = Math.floor(track.scrollWidth / 2);
      track.scrollLeft = half;
    };

    const t1 = setTimeout(init, 0);
    const t2 = setTimeout(init, 250);
    const t3 = setTimeout(init, 900);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [postsLoading, latestLoopPosts.length]);

  useEffect(() => {
    const track = latestTrackRef.current;
    if (!track || postsLoading || latestLoopPosts.length === 0) return;

    let rafId = 0;
    let last = performance.now();
    const SPEED = 0.55;

    const tick = (now) => {
      const dt = now - last;
      last = now;

      if (!latestPaused) track.scrollLeft -= SPEED * dt;

      const half = track.scrollWidth / 2;
      if (half > 0 && track.scrollLeft <= 0) {
        track.scrollLeft = half;
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [postsLoading, latestLoopPosts.length, latestPaused]);

  const getRecentlyViewed = () => {
    try {
      const raw = JSON.parse(localStorage.getItem("recentlyViewed") || "[]");
      return Array.isArray(raw) ? raw : [];
    } catch {
      return [];
    }
  };

  const [recentlyViewed, setRecentlyViewed] = useState(() => getRecentlyViewed());

  useEffect(() => {
    const update = () => setRecentlyViewed(getRecentlyViewed());
    window.addEventListener("recentlyViewedUpdated", update);
    window.addEventListener("storage", update);
    return () => {
      window.removeEventListener("recentlyViewedUpdated", update);
      window.removeEventListener("storage", update);
    };
  }, []);

  const parseUGX = (v) => {
    if (v == null) return 0;
    if (typeof v === "number") return Number.isFinite(v) ? v : 0;
    const s = String(v);
    const nums = s.match(/\d[\d,]*/g);
    if (!nums || !nums.length) return 0;
    return Number(nums[0].replace(/,/g, "")) || 0;
  };

  const topCatsTrackRef = useRef(null);

  const scrollTopCats = (direction) => {
    const el = topCatsTrackRef.current;
    if (!el) return;

    const amount = el.clientWidth * 0.9;
    el.scrollBy({
      left: direction === "next" ? amount : -amount,
      behavior: "smooth",
    });
  };

  const RECENT_VIEWED_UI = useMemo(() => {
    const arr = Array.isArray(recentlyViewed) ? recentlyViewed : [];

    return arr
      .sort((a, b) => (b?.viewedAt || 0) - (a?.viewedAt || 0))
      .slice(0, 12)
      .map((it) => {
        const id = it?.id || it?._id || it?.productId;
        const slug = it?.slug || "";

        if (it?.raw) {
          const pr = resolvePriceUI(it.raw);
          return {
            id,
            _id: id,
            slug: slug || it?.raw?.slug || "",
            name: it?.name || it?.title || it.raw?.title || "Product",
            img: it?.img || getFirstImageUrl(it.raw?.images) || product1,
            price: pr.priceText,
            oldPrice: pr.oldText,
            rating: Number(it?.rating || it.raw?.totalrating || 0),
            raw: it.raw,
          };
        }

        const disc = parseUGX(it?.discountedPrice ?? it?.nowPrice ?? it?.price);
        const old = parseUGX(it?.oldPrice ?? it?.originalPrice);
        const hasDiscount = disc > 0 && old > 0 && disc < old;

        return {
          id,
          _id: id,
          slug,
          name: it?.name || it?.title || "Product",
          img: it?.img || product1,
          price: hasDiscount
            ? `UGX (${disc.toLocaleString()}-${old.toLocaleString()})`
            : formatUGX(disc || old),
          oldPrice: "",
          rating: Number(it?.rating || 0),
        };
      });
  }, [recentlyViewed]);

  const LAST_SEARCHED_UI = useMemo(() => {
    const list = Array.isArray(allProducts) ? allProducts : [];
    const last = Array.isArray(searchHistory) ? searchHistory[0] : null;
    const termRaw = String(last?.q || last?.term || last?.search || last?.query || last || "").trim();
    const term = buildLooseSearch(termRaw);
    if (!term) return [];
    const tokens = term.split(" ").filter(Boolean);

    return list
      .filter((p) => {
        const title = buildLooseSearch(p?.title);
        return tokens.every((tk) => title.includes(tk));
      })
      .slice(0, 12)
      .map(toCard);
  }, [allProducts, searchHistory, toCard]);

  const goToProduct = (item) => {
    const slug = getProductSlug(item);
    if (slug) navigate(`/product/${slug}`);
  };

  const goToSingleFromItem = useCallback(
    (item) => {
      const p = item?.raw || item || {};
      const slug = p?.slug || item?.slug || "";
      if (slug) navigate(`/product/${slug}`);
    },
    [navigate]
  );

  const TrendDropdown = ({ item, sectionKey }) => {
    const key = `${sectionKey}-${item.id || item._id}`;
    const isOpen = openMenuKey === key;
    const disabledWish = isInWishlist(item);

    if (isApp) return null;

    return (
      <div
        className="trend-menuWrap"
        ref={isOpen ? menuWrapRef : null}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="trend-menuBtn"
          onClick={(e) => {
            e.stopPropagation();
            setOpenMenuKey(isOpen ? null : key);
          }}
          aria-haspopup="menu"
          aria-expanded={isOpen ? "true" : "false"}
        >
          ☰
        </button>

        {isOpen && (
          <div className="trend-dropdown" role="menu">
            <button
              type="button"
              className="trend-dropItem"
              role="menuitem"
              disabled={disabledWish}
              aria-disabled={disabledWish ? "true" : "false"}
              style={disabledWish ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
              onClick={(e) => {
                e.stopPropagation();
                if (disabledWish) return;
                onWishlist(item);
                setOpenMenuKey(null);
              }}
            >
              <span className="trend-dropIcon">
                <IoIosHeartEmpty />
              </span>
              <span>{disabledWish ? "In Wishlist" : "Favourite"}</span>
            </button>

            <button
              type="button"
              className="trend-dropItem"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();

                if (sectionKey === "stationery" || sectionKey === "recent") {
                  goToSingleFromItem(item);
                  setOpenMenuKey(null);
                  return;
                }

                onAddToCart(item);
                setOpenMenuKey(null);
              }}
            >
              <span className="trend-dropIcon">
                <PiShoppingCartSimpleLight />
              </span>
              <span>Add to Cart</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  const showPopularSkeleton = productsLoading;
  const showStationerySkeleton = productsLoading && STATIONERY_PRODUCTS.length === 0;
  const showDigitalSkeleton = productsLoading && DIGITAL_PRINTING_PRODUCTS.length === 0;
  const showRecentSkeleton = productsLoading && RECENT_VIEWED_UI.length === 0;
  const showPostsSkeleton = postsLoading;
  const shouldShowRecentSection = showRecentSkeleton || RECENT_VIEWED_UI.length > 0;
  const shouldShowLastSearchedSection = productsLoading || LAST_SEARCHED_UI.length > 0;

  const AppHeroSlider = ({
    slides = [],
    index = 0,
    onIndexChange,
    intervalMs = 7000,
    height = 180,
    visibleRatio = 0.86,
    gap = 10,
  }) => {
    const wrapRef = useRef(null);
    const [paused, setPaused] = useState(false);
    const [wrapW, setWrapW] = useState(0);

    const [dragX, setDragX] = useState(0);
    const draggingRef = useRef(false);
    const startXRef = useRef(0);

    const [noAnim, setNoAnim] = useState(false);

    const len = slides?.length || 0;

    const extendedSlides = useMemo(() => {
      if (len <= 1) return slides;
      return [slides[len - 1], ...slides, slides[0]];
    }, [slides, len]);

    const [innerIndex, setInnerIndex] = useState(len > 1 ? 1 : 0);

    useEffect(() => {
      if (len <= 1) return;
      setInnerIndex(index + 1);
    }, [index, len]);

    useEffect(() => {
      if (!wrapRef.current) return;
      const el = wrapRef.current;

      const ro = new ResizeObserver(() => {
        setWrapW(el.getBoundingClientRect().width || 0);
      });

      ro.observe(el);
      setWrapW(el.getBoundingClientRect().width || 0);

      return () => ro.disconnect();
    }, []);

    useEffect(() => {
      if (!len) return;
      const id = setInterval(() => {
        if (paused) return;
        if (len > 1) setInnerIndex((p) => p + 1);
        onIndexChange?.((prev) => (prev + 1) % len);
      }, intervalMs);

      return () => clearInterval(id);
    }, [len, paused, intervalMs, onIndexChange]);

    const clampIndex = (i) => (len ? (i + len) % len : 0);

    const prev = () => {
      if (!len) return;
      if (len > 1) setInnerIndex((p) => p - 1);
      onIndexChange?.((p) => clampIndex(p - 1));
    };

    const next = () => {
      if (!len) return;
      if (len > 1) setInnerIndex((p) => p + 1);
      onIndexChange?.((p) => clampIndex(p + 1));
    };

    const slideW = Math.max(0, wrapW * visibleRatio);
    const sidePad = Math.max(0, (wrapW - slideW) / 2);
    const step = slideW + gap;

    const usedIndex = len > 1 ? innerIndex : index;
    const baseTranslateX = -(usedIndex * step);
    const translateX = baseTranslateX + (draggingRef.current ? dragX : 0);

    const onTouchStart = (e) => {
      if (!len) return;
      draggingRef.current = true;
      setPaused(true);
      setDragX(0);
      startXRef.current = e.touches[0].clientX;
    };

    const onTouchMove = (e) => {
      if (!draggingRef.current) return;
      const x = e.touches[0].clientX;
      setDragX(x - startXRef.current);
    };

    const onTouchEnd = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;

      const SWIPE = Math.max(45, slideW * 0.15);
      if (dragX > SWIPE) prev();
      else if (dragX < -SWIPE) next();

      setDragX(0);
      setPaused(false);
    };

    const onTrackTransitionEnd = () => {
      if (len <= 1) return;

      if (innerIndex === 0) {
        setNoAnim(true);
        setInnerIndex(len);
        requestAnimationFrame(() => setNoAnim(false));
      }

      if (innerIndex === len + 1) {
        setNoAnim(true);
        setInnerIndex(1);
        requestAnimationFrame(() => setNoAnim(false));
      }
    };

    return (
      <div
        className="app-hero-slider"
        ref={wrapRef}
        style={{ height }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="app-hero-track"
          onTransitionEnd={onTrackTransitionEnd}
          style={{
            transform: `translate3d(${translateX}px,0,0)`,
            gap: `${gap}px`,
            transition: noAnim || draggingRef.current ? "none" : "transform 420ms ease",
            paddingLeft: `${sidePad}px`,
            paddingRight: `${sidePad}px`,
          }}
        >
          {extendedSlides.map((s, i) => {
            const src = s?.src || "";
            const alt = s?.alt || `banner-${i + 1}`;

            return (
              <div
                className="app-hero-slide"
                key={`app-hero-${i}-${src}`}
                style={{ width: `${slideW}px` }}
              >
                <ImgWithLoader
                  src={src}
                  alt={alt}
                  className="app-hero-img"
                  wrapClassName="app-hero-imgWrap"
                  loaderSize={18}
                  imgStyle={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "2px",
                  }}
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const heroHeightVar = "clamp(320px, 36vw, 380px)";
  const sideGap = 10;

  const APP_CATS = useMemo(() => {
    const list = Array.isArray(categories) ? categories : [];

    const pickByTitle = (title) => list.find((c) => normalize(c?.title) === normalize(title));

    const largeFormat =
      pickByTitle("Large Format Print") ||
      pickByTitle("Large Format Printing") ||
      pickByTitle("Large Format") ||
      null;

    const rest = list.filter((c) => String(c?._id) !== String(largeFormat?._id));

    const ordered = [...(largeFormat ? [largeFormat] : []), ...rest].slice(0, 8);

    return [
      { key: "__all__", label: "All", raw: null },
      ...ordered.map((c) => ({
        key: c?._id,
        label: c?.title || "Category",
        raw: c,
      })),
    ];
  }, [categories]);

  const defaultAppActive = APP_CATS?.[0]?.key || "__all__";

  const handleAppCategorySelect = (it) => {
    if (!it || it.key === "__all__") {
      dispatch(setSearchTerm(""));
      dispatch(filterProducts({}));
      navigate("/product");
      return;
    }

    if (it?.raw?._id) {
      dispatch(setSearchTerm(""));
      dispatch(filterProducts({ category: it.raw._id }));
      navigate(`/product?category=${encodeURIComponent(it.raw._id)}`);
      return;
    }

    navigate("/product");
  };

  const retryHomeData = useCallback(async () => {
  if (typeof window !== "undefined" && !navigator.onLine) {
    setHomeFetchFailed(true);
    return;
  }

  try {
    setHomeFetchFailed(false);

    await Promise.all([
      dispatch(getAllPosters()).unwrap(),
      dispatch(getAllProducts({ page: 1, limit: 5000 })).unwrap(),
      dispatch(getAllPosts()).unwrap(),
      dispatch(fetchCategories()).unwrap(),
    ]);
  } catch (err) {
    console.error("Retry home fetch failed:", err);
    setHomeFetchFailed(true);
  }
}, [dispatch]);

  const StationerySection = (
    <section className="trend-section">
      <div className="hero-container hero-containers">
        <div className="trend-header">
          <div className="pop-title-wrap">
            <h2 className="trend-title">Stationery Supply</h2>
            <span className="pop-title-underline" />
          </div>

          <div className="trend-head-arrows">
            <button
              type="button"
              className="trend-head-arrow"
              onClick={stationerySlider.scrollPrev}
              disabled={showStationerySkeleton || !STATIONERY_PRODUCTS.length || !stationerySlider.canLeft}
              aria-label="Previous"
            >
              ‹
            </button>
            <button
              type="button"
              className="trend-head-arrow"
              onClick={stationerySlider.scrollNext}
              disabled={showStationerySkeleton || !STATIONERY_PRODUCTS.length || !stationerySlider.canRight}
              aria-label="Next"
            >
              ›
            </button>
          </div>
        </div>

        <div className="na-head">
          <h3 className="na-title">Stationery</h3>

          <button
            type="button"
            className="na-seeall"
            onClick={() => navigate("/product")}
            aria-label="See all new arrivals"
          >
            See all <span className="na-arrow">›</span>
          </button>
        </div>

        <div
          className="trend-track"
          ref={stationeryTrackRef}
          onMouseEnter={() => stationerySlider.setPaused(true)}
          onMouseLeave={() => stationerySlider.setPaused(false)}
          onTouchStart={() => stationerySlider.setPaused(true)}
          onTouchEnd={() => stationerySlider.setPaused(false)}
        >
          {showStationerySkeleton ? (
            <TrendSkeletonRow count={8} />
          ) : STATIONERY_PRODUCTS.length ? (
            STATIONERY_PRODUCTS.map((item) => {
              const ratingNum = showStars ? Number(item?.raw?.totalrating ?? item?.rating ?? 0) || 0 : 0;

              return (
                <article
                  key={`stationery-${item.id}`}
                  className="trend-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => goToProduct(item)}
                  onKeyDown={(e) => e.key === "Enter" && goToProduct(item)}
                >
                  <TrendDropdown item={item} sectionKey="stationery" />

                  <div className="trend-img-wrap">
                    <ImgWithLoader
                      src={item.img}
                      alt={item.name}
                      loaderSize={18}
                      style={{ width: "100%", height: "100%" }}
                      imgStyle={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </div>

                  <div className="hp-popBody1">
                    <div className="hp-popTitleRow1">
                      <div className="hp-popTitle1" title={item.name}>
                        {item.name}
                      </div>

                      {showStars ? (
                        <div className="hp-popRating1">
                          <span className="hp-popStarIcon1">★</span>
                          <span className="hp-popRateText1">({ratingNum.toFixed(1)})</span>
                        </div>
                      ) : null}
                    </div>

                    <div className="hp-popPrices1">
                      <span className="hp-popNow1">{item.price}</span>
                      {item.oldPrice ? <span className="hp-popOld1">{item.oldPrice}</span> : null}
                    </div>
                  </div>
                </article>
              );
            })
          ) : (
            <div style={{ padding: "12px 0", color: "#777" }}>No Stationery products found.</div>
          )}
        </div>
      </div>
    </section>
  );

  return (
    <>

      {(!isOnline || homeFetchFailed) ? (
        <OfflineOverlay
          show
          onRetry={retryHomeData}
          onReload={() => window.location.reload()}
          message="You are offline. Please check your internet connection."
        />
      ) : null}
      

      {wishToast.show && (
        <div className={`ku-wish-toast ${wishToast.type || "success"}`} role="status" aria-live="polite">
          {wishToast.text}
        </div>
      )}

      <section className="hero hero-app">
        <div className="app-wrapper">
          <div className="app-wrap">
            <div className="full-bleed">
              <div className="app-banner">
                <div className="app-hero-wrap">
                  <div className="app-banner-media">
                    <div
                      className="app-banner-frame"
                      style={{
                        "--app-visibleRatio": 0.86,
                        "--app-gap": "8px",
                      }}
                    >
                      <AppHeroSlider
                        slides={APP_MAIN_SLIDES}
                        index={mainIndex % APP_MAIN_SLIDES.length}
                        onIndexChange={(updater) => {
                          setMainIndex((prev) => (typeof updater === "function" ? updater(prev) : updater));
                        }}
                        intervalMs={5000}
                        height={180}
                        visibleRatio={0.86}
                        gap={8}
                      />
                    </div>
                  </div>

                  <div className="app-hero-dots-out">
                    {APP_MAIN_SLIDES.map((_, i) => (
                      <button
                        key={i}
                        className={`app-hero-dot ${
                          i === mainIndex % APP_MAIN_SLIDES.length ? "is-active" : ""
                        }`}
                        onClick={() => setMainIndex(i)}
                        type="button"
                        aria-label={`Slide ${i + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="app-pad">
              {categoriesLoading ? (
                <AppCategorySkeleton count={7} />
              ) : (
                <AppCategorySection
                  items={APP_CATS}
                  defaultActive={defaultAppActive}
                  onSelect={handleAppCategorySelect}
                />
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="hero">
        <div className="hero-container">
          <div className="hero-grid" style={{ "--heroH": heroHeightVar, "--sideGap": `${sideGap}px` }}>
            <div className="hero-left-spacer" />

            <article className="hero-main-card">
              <div className="hero-main-inner">
                <div className="hero-main-image" style={{ background: "#0b0b0b", height: "var(--heroH)" }}>
                  <FadeImage
                    src={MAIN_SLIDES[mainIndex].src}
                    alt="Kupto Hero"
                    className="hero-slide hero-slide-main"
                    duration={600}
                    background="#0b0b0b"
                    objectFit="cover"
                    fallbackSrc={heroMain1}
                  />

                  <div className={`hero-overlay hero-overlay--${HERO_OVERLAY.align}`}>
                    <h2 className="hero-overlay-title">{HERO_OVERLAY.title}</h2>
                    <p className="hero-overlay-sub">{HERO_OVERLAY.subtitle}</p>
                    <button className="p-but" type="button" onClick={() => navigate("/print")}>
                      Print Now
                    </button>
                  </div>

                  <div className="hero-dots hero-dots-overlay">
                    {MAIN_SLIDES.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`hero-dot ${i === mainIndex ? "active" : ""}`}
                        onClick={() => setMainIndex(i)}
                        aria-label={`Go to slide ${i + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </article>

            <aside
              className="hero-side"
              style={{
                height: "var(--heroH)",
                display: "flex",
                flexDirection: "column",
                gap: "var(--sideGap)",
              }}
            >
              <article
                className="hero-side-card hero-side-top hero-mini"
                style={{
                  height: "calc((var(--heroH) - var(--sideGap)) / 2)",
                  overflow: "hidden",
                  position: "relative",
                  borderRadius: "2px",
                }}
                role="button"
                tabIndex={0}
                onClick={() => navigate("/product")}
                onKeyDown={(e) => e.key === "Enter" && navigate("/product")}
              >
                <div
                  className="hero-side-image hero-mini-media"
                  style={{ background: "#0b0b0b", width: "100%", height: "100%" }}
                >
                  <FadeImage
                    src={SIDE_TOP_IMAGES[topIndex]}
                    alt="Corporate Gifts"
                    className="hero-slide hero-slide-side"
                    duration={550}
                    background="#0b0b0b"
                    objectFit="cover"
                    fallbackSrc={sideTop1}
                  />
                </div>

                <div className="hero-mini-overlay">
                  <div className="hero-mini-chip">Fast delivery</div>
                  <h4 className="hero-mini-title">Corporate Gifts</h4>
                  <p className="hero-mini-sub">Branded bottles for teams</p>

                  <button
                    className="hero-mini-cta"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/product");
                    }}
                  >
                    Shop now →
                  </button>
                </div>
              </article>

              <article
                className="hero-side-card hero-side-bottom hero-mini"
                style={{
                  height: "calc((var(--heroH) - var(--sideGap)) / 2)",
                  overflow: "hidden",
                  position: "relative",
                  borderRadius: "2px",
                }}
                role="button"
                tabIndex={0}
                onClick={() => navigate("/product")}
                onKeyDown={(e) => e.key === "Enter" && navigate("/product")}
              >
                <div
                  className="hero-side-image hero-mini-media"
                  style={{ background: "#0b0b0b", width: "100%", height: "100%" }}
                >
                  <FadeImage
                    src={SIDE_BOTTOM_IMAGES[bottomIndex]}
                    alt="Brand Your Bottle"
                    className="hero-slide hero-slide-side"
                    duration={550}
                    background="#0b0b0b"
                    objectFit="cover"
                    fallbackSrc={sideBottom1}
                  />
                </div>

                <div className="hero-mini-overlay">
                  <div className="hero-mini-chip">Start order</div>
                  <h4 className="hero-mini-title">Brand Yourself</h4>
                  <p className="hero-mini-sub">Upload logo & we print for you</p>

                  <button
                    className="hero-mini-cta"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/product");
                    }}
                  >
                    Order now →
                  </button>
                </div>
              </article>
            </aside>
          </div>
        </div>
      </section>

      <section className="hero-services">
        <div className="hero-container hero-services-row">
          {SERVICE_ITEMS.map((item) => (
            <div className="hero-service-item" key={item.title}>
              <div className="hero-service-icon">{item.icon}</div>
              <div className="hero-service-text">
                <h4>{item.title}</h4>
                <p>{item.subtitle}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="pop-section">
        <div className="hero-container">
          <div className="pop-header">
            <div className="pop-title-wrap">
              <h2 className="pop-title">Popular Product</h2>
              <span className="pop-title-underline" />
            </div>

            <div className="pop-tabs">
              {TAB_LABELS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`pop-tab ${activeTab === tab.key ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}

              <div className="pop-arrows">
                <button
                  type="button"
                  className="pop-arrow-btn"
                  onClick={goPopularPrev}
                  disabled={popularAll.length <= POPULAR_PAGE_SIZE}
                  aria-label="Previous popular products"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="pop-arrow-btn"
                  onClick={goPopularNext}
                  disabled={popularAll.length <= POPULAR_PAGE_SIZE}
                  aria-label="Next popular products"
                >
                  ›
                </button>
              </div>
            </div>
          </div>

          <div className="na-head">
            <h3 className="na-title">New Arrivals</h3>

            <button
              type="button"
              className="na-seeall"
              onClick={() => navigate("/product")}
              aria-label="See all new arrivals"
            >
              See all <span className="na-arrow">›</span>
            </button>
          </div>

          {showPopularSkeleton ? (
            <PopularSkeletonGrid count={10} />
          ) : (
            <div className="hp-popGrid">
              {productList.length ? (
                productList.map((p) => {
                  const key = `popular-${p.id}`;
                  const isOpen = openMenuKey === key;
                  const disabledWish = isInWishlist(p);
                  const slug = getProductSlug(p);

                  return (
                    <div
                      className="hp-popCard"
                      key={p.id}
                      onClick={() => slug && navigate(`/product/${slug}`)}
                      style={{ cursor: slug ? "pointer" : "default" }}
                    >
                      <div className="hp-popTop">
                        {!isApp && (
                          <div
                            className="hp-popMenuWrap"
                            ref={isOpen ? menuWrapRef : null}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              className="hp-popMenuBtn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuKey(isOpen ? null : key);
                              }}
                              aria-haspopup="menu"
                              aria-expanded={isOpen ? "true" : "false"}
                            >
                              ☰
                            </button>

                            {isOpen && (
                              <div className="hp-popDropdown" role="menu">
                                <button
                                  type="button"
                                  className="hp-popDropItem"
                                  role="menuitem"
                                  disabled={disabledWish}
                                  aria-disabled={disabledWish ? "true" : "false"}
                                  style={disabledWish ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (disabledWish) return;
                                    onWishlist(p);
                                    setOpenMenuKey(null);
                                  }}
                                >
                                  <span className="hp-popDropIcon">
                                    <IoIosHeartEmpty />
                                  </span>
                                  <span>{disabledWish ? "In Wishlist" : "Favourite"}</span>
                                </button>

                                <button
                                  type="button"
                                  className="hp-popDropItem"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onAddToCart(p);
                                    setOpenMenuKey(null);
                                  }}
                                >
                                  <span className="hp-popDropIcon">
                                    <PiShoppingCartSimpleLight />
                                  </span>
                                  <span>Add to Cart</span>
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        <ImgWithLoader
                          src={p.img}
                          alt={p.name}
                          className="hp-popImg"
                          wrapClassName="hp-popImgWrap"
                          loaderSize={18}
                          style={{ width: "100%", height: "100%" }}
                          imgStyle={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      </div>

                      <div className="hp-popBody">
                        <div className={`hp-popTitleRow ${!showStars ? "no-rating" : ""}`}>
                          <div className="hp-popTitle">{p.name}</div>

                          {showStars ? (
                            <div className="hp-popRating">
                              <span className="hp-popStarIcon">★</span>
                              <span className="hp-popRateText">
                                ({Number(p?.raw?.totalrating || 0).toFixed(1)})
                              </span>
                            </div>
                          ) : null}
                        </div>

                        <div className="hp-popPrices">
                          <span className="hp-popNow">{p.price}</span>
                          {p.oldPrice && <span className="hp-popOld">{p.oldPrice}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: "12px 0", color: "#777" }}>No products found for this tab.</div>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="promo-section">
        <div className="hero-contain">
          <div className="promo-grid">
            <article className="promo-card promo-card--wide">
              <div className="promo-card-media">
                <img src={promoTopLeft} alt="Printing Machines" />
              </div>
              <div className="promo-card-body">
                <h3 className="promo-title1">Printing Machines</h3>
                <p className="promo-subtitle1">Fast • Quiet • High Quality, Print Smarter. Work Faster</p>

                <button
                  className="promo-btn1"
                  type="button"
                  onClick={() => goToProductsByLatestTitle("Machines")}
                >
                  Shop Now
                </button>
              </div>
            </article>

            <article className="promo-card2 promo-card--wide">
              <div className="promo-card-media2">
                <img src={promoTopMiddle} alt="Go With Trend" className="image2-promo" />
              </div>
              <div className="promo-card-body-w">
                <h3 className="promo-title">Go With Trend</h3>
                <p>Your Style, Upgraded</p>

                <button
                  className="promo-btn"
                  type="button"
                  onClick={() => goToProductsByLatestTitle("shirts")}
                >
                  Shop Now
                </button>
              </div>
            </article>

            <article className="promo-card2 promo-card--wide">
              <div className="promo-card-media2">
                <img src={promoBottomLeft} alt="Branded Items" />
              </div>
              <div className="promo-card-body-w">
                <h3 className="promo-title">Branded Items</h3>
                <p>with, High quality</p>

                <button className="promo-btn" type="button" onClick={() => goToProductsByQuery("cups")}>
                  Shop Now
                </button>
              </div>
            </article>

            <article className="promo-card promo-card--wide">
              <div className="promo-card-media">
                <img src={promoBottomMiddle} alt="Be The Revolution" />
              </div>
              <div className="promo-card-body">
                <h3 className="promo-title">Be The Revolution</h3>
                <p className="promo-subtitle">Professional Self-Inking Stamps • Clear & Long-Lasting Prints</p>

                <button className="promo-btn" type="button" onClick={() => goToProductsByLatestTitle("Stamps")}>
                  Shop Now
                </button>
              </div>
            </article>
          </div>

          <article className="promo-card3 promo-card--tall3">
            <div className="promo-tall-media3">
              <img src={promoTallRight} alt="Promo" />
            </div>
          </article>
        </div>
      </section>

      <section className="app-print-cta" aria-label="Get all your printing and stationery">
        <div className="app-print-cta-inner">
          <div className="app-print-kicker">GET ALL YOUR</div>

          <div className="app-print-head">
            <div className="app-print-big">PRINTING</div>
            <div className="app-print-andwrap">
              <span className="app-print-and" aria-hidden="true">
                &
              </span>
              <span className="app-print-small">STATIONERY</span>
            </div>
          </div>

          <div className="app-print-sub">
            on your smartphone <br />
            with just one <br />
            <span className="app-print-click">
              CLICK <TbHandClick className="app-print-clickIcon" aria-hidden="true" focusable="false" />
            </span>
          </div>

          <button type="button" className="app-print-btn" onClick={() => navigate("/print")} aria-label="Print now">
            PRINT NOW
          </button>
        </div>
      </section>

      {isApp ? StationerySection : null}

      <section className="kcats-section topcatlarge">
        <div className="hero-container">
          <div className="trend-header">
            <div className="pop-title-wrap">
              <h2 className="trend-title">Top Categories</h2>
              <span className="pop-title-underline" />
            </div>

            <div className="trend-head-arrows">
              <button
                type="button"
                className="trend-head-arrow"
                onClick={() => scrollTopCats("prev")}
                aria-label="Previous categories"
              >
                ‹
              </button>
              <button
                type="button"
                className="trend-head-arrow"
                onClick={() => scrollTopCats("next")}
                aria-label="Next categories"
              >
                ›
              </button>
            </div>
          </div>

          {categoriesLoading ? (
            <CategorySkeletonGrid count={10} />
          ) : (
            <div className="kcats-scroll-wrap">
              <div className="kcats-scroll-track" ref={topCatsTrackRef}>
                {categories.map((cat) => (
                  <article
                    key={cat?._id}
                    className="kcats-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleCategoryClick(cat)}
                    onKeyDown={(e) => e.key === "Enter" && handleCategoryClick(cat)}
                  >
                    <div className="kcats-text">
                      <h3 className="kcats-name">{cat?.title}</h3>
                      <span className="kcats-cta">Shop Now</span>
                    </div>

                    <div className="kcats-img-wrap">
                      <img
                        src={cat?.images?.[0]?.url || "/images/placeholder.png"}
                        alt={cat?.title || "Category"}
                        loading="lazy"
                      />
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {shouldShowLastSearchedSection ? (
        <section className="trend-section">
          <div className="hero-container hero-containers">
            <div className="trend-header">
              <div className="pop-title-wrap">
                <h2 className="trend-title">Last Searched</h2>
                <span className="pop-title-underline" />
              </div>

              <div className="trend-head-arrows">
                <button
                  type="button"
                  className="trend-head-arrow"
                  onClick={() => scrollTrack(lastSearchedTrackRef, "prev")}
                  disabled={!LAST_SEARCHED_UI.length}
                  aria-label="Previous"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="trend-head-arrow"
                  onClick={() => scrollTrack(lastSearchedTrackRef, "next")}
                  disabled={!LAST_SEARCHED_UI.length}
                  aria-label="Next"
                >
                  ›
                </button>
              </div>
            </div>

            <div className="na-head">
              <h3 className="na-title">Last Searched</h3>

              <button
                type="button"
                className="na-seeall"
                onClick={() => navigate("/product")}
              >
                See all <span className="na-arrow">›</span>
              </button>
            </div>

            <div className="trend-track" ref={lastSearchedTrackRef}>
              {productsLoading ? (
                <TrendSkeletonRow count={8} />
              ) : LAST_SEARCHED_UI.length ? (
                LAST_SEARCHED_UI.map((item) => {
                  const ratingNum = showStars ? Number(item?.raw?.totalrating ?? item?.rating ?? 0) || 0 : 0;

                  return (
                    <article
                      key={`last-${item.id}`}
                      className="trend-card"
                      role="button"
                      tabIndex={0}
                      onClick={() => goToProduct(item)}
                      onKeyDown={(e) => e.key === "Enter" && goToProduct(item)}
                    >
                      <TrendDropdown item={item} sectionKey="last" />
                      <div className="trend-img-wrap">
                        <ImgWithLoader
                          src={item.img}
                          alt={item.name}
                          loaderSize={18}
                          style={{ width: "100%", height: "100%" }}
                          imgStyle={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      </div>
                      <div className="hp-popBody1">
                        <div className="hp-popTitleRow1">
                          <div className="hp-popTitle1" title={item.name}>
                            {item.name}
                          </div>

                          {showStars ? (
                            <div className="hp-popRating1">
                              <span className="hp-popStarIcon1">★</span>
                              <span className="hp-popRateText1">({ratingNum.toFixed(1)})</span>
                            </div>
                          ) : null}
                        </div>

                        <div className="hp-popPrices1">
                          <span className="hp-popNow1">{item.price}</span>
                          {item.oldPrice ? <span className="hp-popOld1">{item.oldPrice}</span> : null}
                        </div>
                      </div>
                    </article>
                  );
                })
              ) : (
                <div style={{ padding: "12px 0", color: "#777" }}>
                  No last searched products yet.
                </div>
              )}
            </div>
          </div>
        </section>
      ) : null}

      <section className="hg-section">
        <div className="hg-wrap">
          <div className="hg-grid">
            {postsLoading2 ? (
              <>
                <div className="hg-item">
                  <div className="skel skel-fill" />
                </div>
                <div className="hg-item">
                  <div className="skel skel-fill" />
                </div>
                <div className="hg-item hg-item-tall">
                  <div className="skel skel-fill" />
                </div>
                <div className="hg-item hg-item-wide">
                  <div className="skel skel-fill" />
                </div>
              </>
            ) : (
              (postTiles || [])
                .filter((t) => Boolean(t?.imageUrl))
                .slice(0, 4)
                .map((tile, idx) => {
                  const classes =
                    idx === 2
                      ? "hg-item hg-item-tall"
                      : idx === 3
                      ? "hg-item hg-item-wide"
                      : "hg-item";

                  return (
                    <article
                      key={tile?._key || idx}
                      className={classes}
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate("/product")}
                      onKeyDown={(e) => e.key === "Enter" && navigate("/product")}
                      style={{ cursor: "pointer" }}
                    >
                      <img src={tile.imageUrl} alt={tile.title || "Post"} />

                      <div className="hg-cap">
                        <h4 className="hg-title">{tile.title || "Untitled"}</h4>
                      </div>
                    </article>
                  );
                })
            )}
          </div>
        </div>
      </section>

      {!isApp ? StationerySection : null}

      <section className="hero mb-40">
        <div className="deals deals-electronics">
          <div className="hero-container deals-inner">
            <div className="deals-header deals-header-electronics">
              <div className="deals-left">
                <h2 className="deals-title">Featured Deals</h2>
                <span className="deals-title-accent" />
              </div>

              <div className="deals-right">
                <button
                  className="deals-view-all deals-view-all-dark"
                  type="button"
                  onClick={handleViewAllLargeFormat}
                >
                  View All Products &rsaquo;
                </button>
              </div>
            </div>

            {productsLoading || showDealsSkeleton ? (
              <div className="deals-grid-electronics">
                {Array.from({ length: 6 }).map((_, i) => (
                  <article className="deals-elec-card deals-elec-card-skel" key={`deal-skel-${i}`}>
                    <div className="deals-elec-img">
                      <div className="skel skel-fill" />
                    </div>
                    <div className="deals-elec-body">
                      <div className="skel skel-line skel-line--sm" />
                      <div className="skel skel-line skel-line--md" />
                      <div className="skel skel-line skel-line--sm" />
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="deals-grid-electronics">
                {FEATURED_DEALS.slice(0, 6).map((deal) => {
                  const ratingNum = Number(deal?.raw?.totalrating || 0);
                  const fullStars = Math.max(0, Math.min(5, Math.round(ratingNum)));
                  const slug = getProductSlug(deal);

                  return (
                    <article
                      key={deal.id}
                      className="deals-elec-card"
                      role="button"
                      tabIndex={0}
                      onClick={() => slug && navigate(`/product/${slug}`)}
                      onKeyDown={(e) => e.key === "Enter" && slug && navigate(`/product/${slug}`)}
                      style={{ cursor: slug ? "pointer" : "default" }}
                    >
                      <TrendDropdown item={deal} sectionKey="featured-deals" />

                      <div className="deals-elec-img">
                        <ImgWithLoader
                          src={deal.img}
                          alt={deal.name}
                          className="deals-elec-image"
                          wrapClassName="deals-elec-image-wrap"
                          loaderSize={18}
                          style={{ width: "100%", height: "100%" }}
                          imgStyle={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      </div>

                      <div className="deals-elec-body">
                        {showStars ? (
                          <div className="deals-elec-rating">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <span
                                key={i}
                                className={`deals-elec-star ${i < fullStars ? "is-on" : ""}`}
                              >
                                ★
                              </span>
                            ))}
                          </div>
                        ) : null}

                        <h3 className="deals-elec-name">{deal.name}</h3>

                        <div className="deals-elec-price-row">
                          <span className="deals-elec-price">{deal.price}</span>
                          {deal.oldPrice ? (
                            <span className="deals-elec-old">{deal.oldPrice}</span>
                          ) : null}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {shouldShowRecentSection ? (
        <section className="trend-section">
          <div className="hero-container hero-containers">
            <div className="trend-header">
              <div className="pop-title-wrap">
                <h2 className="trend-title">Recently Viewed Products</h2>
                <span className="pop-title-underline" />
              </div>

              <div className="trend-head-arrows">
                <button
                  type="button"
                  className="trend-head-arrow"
                  onClick={() => scrollTrack(recentTrackRef, "prev")}
                  disabled={!RECENT_VIEWED_UI.length}
                  aria-label="Previous"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="trend-head-arrow"
                  onClick={() => scrollTrack(recentTrackRef, "next")}
                  disabled={!RECENT_VIEWED_UI.length}
                  aria-label="Next"
                >
                  ›
                </button>
              </div>
            </div>

            <div className="na-head">
              <h3 className="na-title">Recently Viewed</h3>

              <button
                type="button"
                className="na-seeall"
                onClick={() => navigate("/product")}
                aria-label="See all new arrivals"
              >
                See all <span className="na-arrow">›</span>
              </button>
            </div>

            <div className="trend-track" ref={recentTrackRef}>
              {showRecentSkeleton ? (
                <TrendSkeletonRow count={8} />
              ) : (
                RECENT_VIEWED_UI.map((item) => {
                  const ratingNum = showStars ? Number(item?.rating ?? 0) || 0 : 0;

                  return (
                    <article
                      key={`recent-${item.id}`}
                      className="trend-card"
                      role="button"
                      tabIndex={0}
                      onClick={() => goToProduct(item)}
                      onKeyDown={(e) => e.key === "Enter" && goToProduct(item)}
                    >
                      <TrendDropdown item={item} sectionKey="recent" />
                      <div className="trend-img-wrap">
                        <ImgWithLoader
                          src={item.img}
                          alt={item.name}
                          loaderSize={18}
                          style={{ width: "100%", height: "100%" }}
                          imgStyle={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      </div>
                      <div className="hp-popBody1">
                        <div className="hp-popTitleRow1">
                          <div className="hp-popTitle1" title={item.name}>
                            {item.name}
                          </div>

                          {showStars ? (
                            <div className="hp-popRating1">
                              <span className="hp-popStarIcon1">★</span>
                              <span className="hp-popRateText1">({ratingNum.toFixed(1)})</span>
                            </div>
                          ) : null}
                        </div>

                        <div className="hp-popPrices1">
                          <span className="hp-popNow1">{item.price}</span>
                          {item.oldPrice ? <span className="hp-popOld1">{item.oldPrice}</span> : null}
                        </div>
                      </div>
                    </article>
                  );
                })
              )}
            </div>
          </div>
        </section>
      ) : null}

      <section className="latest-section">
        <div className="hero-container hero-containers">
          <div className="trend-header">
            <div className="pop-title-wrap">
              <h2 className="trend-title">Our Latest Posts</h2>
              <span className="pop-title-underline" />
            </div>
          </div>

          {showPostsSkeleton ? (
            <LatestSkeletonRow count={4} />
          ) : (
            <div
              className="latest-track"
              ref={latestTrackRef}
              onMouseEnter={() => setLatestPaused(true)}
              onMouseLeave={() => setLatestPaused(false)}
              onTouchStart={() => setLatestPaused(true)}
              onTouchEnd={() => setLatestPaused(false)}
            >
              {latestLoopPosts.length ? (
                latestLoopPosts.map((post, idx) => (
                  <article
                    key={`${post?._id || post?.title || post?.date || "post"}-${idx}`}
                    className="latest-card"
                    role="button"
                    tabIndex={0}
                    style={{ cursor: "pointer" }}
                    onClick={() => goToProductsByLatestTitle(post?.title)}
                    onKeyDown={(e) => e.key === "Enter" && goToProductsByLatestTitle(post?.title)}
                  >
                    <div className="latest-image-wrap">
                      <img
                        src={post?.images?.[0]?.url || post?.images?.[0] || "/images/placeholder.png"}
                        alt={post?.title || "Latest post"}
                      />
                    </div>

                    <div className="latest-meta">
                      <p className="latest-date">{formatDate(post?.date)}</p>
                      <h3 className="latest-card-title">{post?.title || "Untitled"}</h3>
                      <p className="latest-subtitle">{stripHtml(post?.description) || "No description available"}</p>

                      <button
                        type="button"
                        className="latest-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          goToProductsByLatestTitle(post?.title);
                        }}
                      >
                        Shop Now
                      </button>
                    </div>
                  </article>
                ))
              ) : (
                <p style={{ padding: "10px 0" }}>No posts available.</p>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="trend-section">
        <div className="hero-container hero-containers">
          <div className="trend-header">
            <div className="pop-title-wrap">
              <h2 className="trend-title">Digital Printing</h2>
              <span className="pop-title-underline" />
            </div>

            <div className="trend-head-arrows">
              <button
                type="button"
                className="trend-head-arrow"
                onClick={digitalSlider.scrollPrev}
                disabled={showDigitalSkeleton || !DIGITAL_PRINTING_PRODUCTS.length || !digitalSlider.canLeft}
                aria-label="Previous"
              >
                ‹
              </button>
              <button
                type="button"
                className="trend-head-arrow"
                onClick={digitalSlider.scrollNext}
                disabled={showDigitalSkeleton || !DIGITAL_PRINTING_PRODUCTS.length || !digitalSlider.canRight}
                aria-label="Next"
              >
                ›
              </button>
            </div>
          </div>

          <div className="na-head">
            <h3 className="na-title">Digital Printing</h3>

            <button
              type="button"
              className="na-seeall"
              onClick={() => navigate("/product")}
              aria-label="See all new arrivals"
            >
              See all <span className="na-arrow">›</span>
            </button>
          </div>

          <div
            className="trend-track"
            ref={digitalTrackRef}
            onMouseEnter={() => digitalSlider.setPaused(true)}
            onMouseLeave={() => digitalSlider.setPaused(false)}
            onTouchStart={() => digitalSlider.setPaused(true)}
            onTouchEnd={() => digitalSlider.setPaused(false)}
          >
            {showDigitalSkeleton ? (
              <TrendSkeletonRow count={8} />
            ) : DIGITAL_PRINTING_PRODUCTS.length ? (
              DIGITAL_PRINTING_PRODUCTS.map((item) => {
                const ratingNum = showStars ? Number(item?.raw?.totalrating ?? item?.rating ?? 0) || 0 : 0;

                return (
                  <article
                    key={`digital-${item.id}`}
                    className="trend-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => goToProduct(item)}
                    onKeyDown={(e) => e.key === "Enter" && goToProduct(item)}
                  >
                    <TrendDropdown item={item} sectionKey="digital" />
                    <div className="trend-img-wrap">
                      <ImgWithLoader
                        src={item.img}
                        alt={item.name}
                        loaderSize={18}
                        style={{ width: "100%", height: "100%" }}
                        imgStyle={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    </div>
                    <div className="hp-popBody1">
                      <div className="hp-popTitleRow1">
                        <div className="hp-popTitle1" title={item.name}>
                          {item.name}
                        </div>

                        {showStars ? (
                          <div className="hp-popRating1">
                            <span className="hp-popStarIcon1">★</span>
                            <span className="hp-popRateText1">({ratingNum.toFixed(1)})</span>
                          </div>
                        ) : null}
                      </div>
                      <div className="hp-popPrices1">
                        <span className="hp-popNow1">{item.price}</span>
                        {item.oldPrice ? <span className="hp-popOld1">{item.oldPrice}</span> : null}
                      </div>
                    </div>
                  </article>
                );
              })
            ) : (
              <div style={{ padding: "12px 0", color: "#777" }}>No Digital Printing products found.</div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;